import { BilletGroup, Location, SpecialLocation } from "../models";

/**
 * Determines if a location should consolidate billets
 * All locations use the same GroupName-based grouping logic
 */
export const shouldLocationConsolidateBillets = (
  location: Location | SpecialLocation,
): boolean => {
  return true;
};

/**
 * Groups billets by heat number with optional consolidation logic
 * This is the centralized function that handles all billet grouping logic
 * Note: The main screen groups by GroupName but displays as heat groups
 */
export const groupBilletsByHeat = (
  groups: BilletGroup[],
  shouldConsolidate: boolean = true,
): Record<string, BilletGroup[]> => {
  if (groups.length === 0) {
    return {};
  }

  // Group by GroupName (this becomes the gray header in main screen)
  const groupedByGroupName = groups.reduce(
    (acc, group) => {
      const groupName = group.GroupName;
      if (!acc[groupName]) {
        acc[groupName] = [];
      }
      acc[groupName].push(group);
      return acc;
    },
    {} as Record<string, BilletGroup[]>,
  );

  // If consolidation is disabled, return groups as-is
  if (!shouldConsolidate) {
    return groupedByGroupName;
  }

  // Apply consolidation logic for locations that support it
  const consolidatedGroups: Record<string, BilletGroup[]> = {};

  Object.keys(groupedByGroupName).forEach((groupName) => {
    const groupBillets = groupedByGroupName[groupName];

    if (groupBillets.length <= 1) {
      // Single group, no consolidation needed
      consolidatedGroups[groupName] = groupBillets;
      return;
    }

    // Group by identical properties (excluding position and count)
    const identicalGroups: Record<string, BilletGroup[]> = {};

    groupBillets.forEach((group) => {
      // Create a key from all properties except BilletsCount and Position
      const groupKey = `${group.HeatNumber}-${group.BilletsQualityStatus}-${group.Section}-${group.Grade}`;

      if (!identicalGroups[groupKey]) {
        identicalGroups[groupKey] = [];
      }
      identicalGroups[groupKey].push(group);
    });

    // Consolidate identical groups
    const consolidatedHeats: BilletGroup[] = [];

    Object.values(identicalGroups).forEach((identicalGroupList) => {
      if (identicalGroupList.length === 1) {
        // Single group, add as-is
        consolidatedHeats.push(identicalGroupList[0]);
      } else {
        // Multiple identical groups, consolidate them
        const representativeGroup = identicalGroupList[0];
        const totalBilletsCount = identicalGroupList.reduce(
          (sum, group) => sum + group.BilletsCount,
          0,
        );
        const highestPosition = Math.max(
          ...identicalGroupList.map((group) => group.Position),
        );

        // Create consolidated group
        const consolidatedGroup: BilletGroup = {
          ...representativeGroup,
          BilletsCount: totalBilletsCount,
          Position: highestPosition,
          HeatNumber: representativeGroup.HeatNumber,
        };

        consolidatedHeats.push(consolidatedGroup);
      }
    });

    consolidatedGroups[groupName] = consolidatedHeats;
  });

  return consolidatedGroups;
};

/**
 * Groups billets for UI display with consecutive GroupName consolidation
 * Consolidates consecutive billets with the same GroupName into single display groups
 * For MergeConsecutiveGroups = 0: No grouping - displays each billet separately by position
 */
export const groupBilletsForDisplay = (
  groups: BilletGroup[],
  location: Location | SpecialLocation,
): Record<string, BilletGroup[]> => {
  if (groups.length === 0) {
    return {};
  }

  const shouldConsolidate = shouldLocationConsolidateBillets(location);

  // For non-consolidating locations, display each billet separately by position
  if (!shouldConsolidate) {
    const positionBasedGroups: Record<string, BilletGroup[]> = {};

    // Sort by position descending (highest first, matching old system)
    const sortedGroups = [...groups].sort((a, b) => b.Position - a.Position);

    sortedGroups.forEach((group, index) => {
      // Create unique key based on position to prevent grouping
      const uniqueKey = `${group.GroupName}_pos_${group.Position}`;
      positionBasedGroups[uniqueKey] = [group];
    });

    return positionBasedGroups;
  }

  // For consolidating locations, implement consecutive GroupName consolidation
  // First, sort all billets by position descending (highest first)
  const sortedGroups = [...groups].sort((a, b) => b.Position - a.Position);

  const consecutiveGroupNameGroups: Record<string, BilletGroup[]> = {};
  let groupCounter = 0;

  for (let i = 0; i < sortedGroups.length; i++) {
    const currentGroup = sortedGroups[i];

    // Find all consecutive billets with the same GroupName
    const consecutiveGroupNameBillets: BilletGroup[] = [currentGroup];
    let nextIndex = i + 1;

    while (nextIndex < sortedGroups.length) {
      const nextGroup = sortedGroups[nextIndex];

      // Check if next billet has same GroupName AND consecutive position
      const isSameGroupName = currentGroup.GroupName === nextGroup.GroupName;
      const isConsecutive =
        consecutiveGroupNameBillets[consecutiveGroupNameBillets.length - 1]
          .Position ===
        nextGroup.Position + 1;

      if (isSameGroupName && isConsecutive) {
        consecutiveGroupNameBillets.push(nextGroup);
        nextIndex++;
      } else {
        break;
      }
    }

    // Create display group with all consecutive billets that share the same GroupName
    const displayKey = `${currentGroup.GroupName}_${groupCounter++}`;
    consecutiveGroupNameGroups[displayKey] = consecutiveGroupNameBillets;

    // Skip the billets we just processed
    i += consecutiveGroupNameBillets.length - 1;
  }

  return consecutiveGroupNameGroups;
};

/**
 * Gets individual billet counts for consecutive groups that were consolidated
 * Returns a Record where keys are display group names and values are pipe-separated strings of individual counts
 * Example: { "BIC 404 180 KY-1018A03_0": "138 | 133 | 120" }
 */
export const getIndividualBilletCounts = (
  groups: BilletGroup[],
  location: Location | SpecialLocation,
): Record<string, string> => {
  if (groups.length === 0) {
    return {};
  }

  const shouldConsolidate = shouldLocationConsolidateBillets(location);
  const result: Record<string, string> = {};

  // If consolidation is disabled, no individual counts to show
  if (!shouldConsolidate) {
    return result;
  }

  // Implement the same consecutive GroupName grouping logic as groupBilletsForDisplay
  const sortedGroups = [...groups].sort((a, b) => b.Position - a.Position);
  let groupCounter = 0;

  for (let i = 0; i < sortedGroups.length; i++) {
    const currentGroup = sortedGroups[i];

    // Find all consecutive billets with the same GroupName
    const consecutiveGroupNameBillets: BilletGroup[] = [currentGroup];
    let nextIndex = i + 1;

    while (nextIndex < sortedGroups.length) {
      const nextGroup = sortedGroups[nextIndex];

      // Check if next billet has same GroupName AND consecutive position
      const isSameGroupName = currentGroup.GroupName === nextGroup.GroupName;
      const isConsecutive =
        consecutiveGroupNameBillets[consecutiveGroupNameBillets.length - 1]
          .Position ===
        nextGroup.Position + 1;

      if (isSameGroupName && isConsecutive) {
        consecutiveGroupNameBillets.push(nextGroup);
        nextIndex++;
      } else {
        break;
      }
    }

    // Only show individual counts if multiple consecutive billets with same GroupName were consolidated
    // COMMENTED OUT: Hide individual billets count display (61 | 65 | 63 format)
    // if (consecutiveGroupNameBillets.length > 1) {
    //   const displayKey = `${currentGroup.GroupName}_${groupCounter}`;
    //   const individualCounts = consecutiveGroupNameBillets.map(
    //     (group) => group.BilletsCount,
    //   );
    //   result[displayKey] = individualCounts.join(" | ");
    // }

    groupCounter++;
    // Skip the billets we just processed
    i += consecutiveGroupNameBillets.length - 1;
  }

  return result;
};

/**
 * Sorts heat numbers by their highest position (legacy behavior)
 */
export const sortHeatsByPosition = (
  groupedByHeat: Record<string, BilletGroup[]>,
): string[] => {
  return Object.keys(groupedByHeat).sort((a, b) => {
    const aMaxPosition = Math.max(
      ...groupedByHeat[a].map((group) => group.Position),
    );
    const bMaxPosition = Math.max(
      ...groupedByHeat[b].map((group) => group.Position),
    );
    return bMaxPosition - aMaxPosition; // Descending order (highest position first)
  });
};
